import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useForm, useWatch } from "antd/es/form/Form";
import { addSeconds, isFuture, isToday } from "date-fns/esm";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import {
  EntityType,
  EntityTypesSupportingRoutes,
  Route,
} from "@mapmycustomers/shared/types/entity";
import { RoutePreview } from "@mapmycustomers/shared/types/entity/Route";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";

import BulkActivityCreateModal from "@app/component/createEditEntity/Activity/BulkActivityCreateModal";
import FormValues from "@app/component/createEditEntity/Activity/types/FormValues";
import convertFieldToLayoutSchemaField from "@app/component/FormFields/utils/convertFieldToLayoutSchemaField";
import { getActivityTypes } from "@app/store/activity";
import { RootState } from "@app/store/rootReducer";
import { fetchRoute, fetchRoutePreview } from "@app/store/routes/actions";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { parseApiDate } from "@app/util/parsers";
import useHiddenChat from "@app/util/useHiddenChat";

import styles from "./Common.module.scss";

type BulkCreateActivityPageProps = typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>;

const BulkCreateActivityPage: React.FC<BulkCreateActivityPageProps> = ({
  activityTypes,
  onFetchRoute,
  onFetchRoutePreview,
}) => {
  const intl = useIntl();
  const { entityType, rid } = useParams<{ entityType: EntityTypesSupportingRoutes; rid: string }>();

  const [route, setRoute] = useState<Route | undefined>();
  const [routePreview, setRoutePreview] = useState<RoutePreview>();

  const [form] = useForm<FormValues>();
  const activityTypeId = useWatch("activityTypeId", form);
  const startAt = useWatch("startAt", form);
  const completed = useWatch("completed", form);

  useEffect(() => {
    onFetchRoute({ id: parseInt(rid, 10), callback: setRoute, entityType });
  }, [entityType, onFetchRoute, rid]);

  useEffect(() => {
    if (route) {
      const previewPayload = {
        endGeoPoint: route.routeDetail.endGeoPoint,
        routeAccounts:
          entityType === EntityType.COMPANY
            ? (route.routeAccounts ?? []).map(({ id }) => id)
            : undefined,
        routeContacts:
          entityType === EntityType.PERSON
            ? (route.routeContacts ?? []).map(({ id }) => id)
            : undefined,
        routeType: route.routeDetail.type,
        startGeoPoint: route.routeDetail.startGeoPoint,
      };
      onFetchRoutePreview({ callback: setRoutePreview, entityType, payload: previewPayload });
    }
  }, [entityType, onFetchRoute, onFetchRoutePreview, route]);

  const isFutureDate = useMemo(
    () => startAt && (isFuture(startAt) || isToday(startAt)) && !completed,
    [completed, startAt]
  );

  const activityTypeName = useMemo(
    () => activityTypes.find((activityType) => activityType.id === activityTypeId)?.name,
    [activityTypeId, activityTypes]
  );

  const routeEntities = useMemo(
    () =>
      (route?.routeType === EntityType.COMPANY_ROUTE
        ? route?.routeAccounts
        : route?.routeContacts) ?? [],
    [route?.routeAccounts, route?.routeContacts, route?.routeType]
  );

  const activities = useMemo(() => {
    if (!route || !routePreview) {
      return [];
    }

    const allottedTimeMap = new Map(
      route.stopTime.map((row) => [row.accountId ?? row.contactId, row.allottedTime])
    );
    const arrivalTimeMap = new Map(
      (routePreview.accounts ?? routePreview.contacts ?? []).map((row) => [
        row.id ?? row.id,
        row.arrivalTime,
      ])
    );
    let cumulativeSeconds = 0;

    return routeEntities.map(({ id, name }) => {
      const timeForCurrent = allottedTimeMap.get(id) ?? 0;
      const arrivalTime = arrivalTimeMap.get(id)!;
      const startAt = addSeconds(parseApiDate(arrivalTime), cumulativeSeconds);
      cumulativeSeconds += timeForCurrent;

      return {
        ...(route.routeType === EntityType.COMPANY_ROUTE
          ? { account: { id } }
          : { contact: { id } }),
        name:
          activityTypeName && name
            ? intl.formatMessage(
                {
                  id: "web1.bulkCreateActivityPage.autoFillName",
                  defaultMessage: "{activityTypeName} with {name}",
                  description: "Default activity name",
                },
                { activityTypeName, name }
              )
            : "",
        startAt: startAt.toISOString(),
      };
    });
  }, [activityTypeName, intl, route, routeEntities, routePreview]);

  const lockedFieldsProps = useMemo(
    () => ({
      [ActivityFieldName.COMPANY]:
        route?.routeType === EntityType.COMPANY_ROUTE
          ? {
              placeholder: intl.formatMessage({
                id: "web1.bulkCreateActivityPage.autoFilledWithStops",
                defaultMessage: "Autofilled with stops",
                description: "Relationship field placeholder",
              }),
            }
          : {},
      [ActivityFieldName.NAME]: {
        caption: intl.formatMessage({
          id: "web1.bulkCreateActivityPage.field.activityName.caption",
          defaultMessage: "Based on activity type and relationship",
          description: "Activity name field caption",
        }),
      },
      [ActivityFieldName.PERSON]:
        route?.routeType === EntityType.PEOPLE_ROUTE
          ? {
              placeholder: intl.formatMessage({
                id: "web1.bulkCreateActivityPage.autoFilledWithStops",
                defaultMessage: "Autofilled with stops",
                description: "Relationship field placeholder",
              }),
            }
          : {},
    }),
    [intl, route?.routeType]
  );

  const handleHide = useCallback(
    (created?: boolean) => {
      window.parent.postMessage(
        `closeCreateBulkActivityModal${JSON.stringify(
          created ? { count: activities.length, isFutureDate } : {}
        )}`,
        "*"
      );
    },
    [activities.length, isFutureDate]
  );

  const isFieldDisabled = useCallback((field: IField) => {
    if (!field.hasFeature(FieldFeature.RELATIONSHIPS)) {
      return false;
    }
    return true;
  }, []);

  const defaultAddedFields = useMemo(() => {
    const field = activityFieldModel.getByName(
      route?.routeType === EntityType.COMPANY_ROUTE
        ? ActivityFieldName.COMPANY
        : ActivityFieldName.PERSON
    );

    if (!field) {
      return [];
    }

    return [convertFieldToLayoutSchemaField(field)];
  }, [route?.routeType]);

  useHiddenChat();

  if (!route || !routePreview) {
    return null;
  }

  return (
    <BulkActivityCreateModal
      activities={activities}
      className={styles.modalContainer}
      count={routeEntities.length}
      defaultAddedFields={defaultAddedFields}
      form={form}
      initialValue={{ startAt: route.routeDetail.startAt }}
      isFieldDisabled={isFieldDisabled}
      lockedFieldsProps={lockedFieldsProps}
      onHide={handleHide}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
});

const mapDispatchToProps = {
  onFetchRoute: fetchRoute.request,
  onFetchRoutePreview: fetchRoutePreview.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkCreateActivityPage);
